import type { LayoutSize, SlideImageLayoutProps } from './types';
import { getHasFullWidthImageLayout, isFullSizeImageSlide } from './utils';
import { getLayoutSize } from './getLayoutSize';

interface SlideImageConfiguration {
  hasImage: boolean;
  containedFullBackgroundImage?: {
    altText: string;
    backgroundImage: string;
    backgroundImageSize: 'contain';
  };
  fullBackgroundImage?: {
    altText: string;
    backgroundImage: string;
    backgroundImageSize: 'cover';
    shouldShowColorOverlay: boolean;
  };
  partialImageLayout?: {
    image: {
      url: string;
      altText: string;
    };
    hasImageInset: boolean;
    snapDirection: 'x' | 'y';
    imagePosition: 'start' | 'end';
    layoutSize: LayoutSize;
  };
}

export const getSlideImageConfiguration = ({
  image,
  staticContent,
  hasInteractiveContents,
}: SlideImageLayoutProps): SlideImageConfiguration => {
  if (!image.url) {
    return {
      hasImage: false,
    };
  }

  const isDocumentSlide = staticContent.type === 'document';
  if (isDocumentSlide) {
    return {
      hasImage: true,
      containedFullBackgroundImage: {
        altText: image.altText ?? '',
        backgroundImage: image.url,
        backgroundImageSize: 'contain',
      },
    };
  }

  if (isFullSizeImageSlide(staticContent)) {
    return {
      hasImage: true,
      fullBackgroundImage: {
        altText: image.altText ?? '',
        backgroundImage: image.url,
        backgroundImageSize: 'cover' as const,
        shouldShowColorOverlay: false,
      },
    };
  }

  const hasFullWidthImageLayout = getHasFullWidthImageLayout(staticContent);
  if (hasFullWidthImageLayout) {
    return {
      hasImage: true,
      fullBackgroundImage: {
        altText: image.altText ?? '',
        backgroundImage: image.url,
        backgroundImageSize: 'cover' as const,
        shouldShowColorOverlay: true,
      },
    };
  }

  if ('image' in staticContent) {
    const isDefaultNoImageLayout =
      staticContent.image.imageLayoutPosition.start === 0 &&
      staticContent.image.imageLayoutPosition.end === 0;
    if (isDefaultNoImageLayout) {
      return {
        hasImage: false,
      };
    }

    const { snapDirection } = staticContent.layout;
    const imagePosition =
      staticContent.image.imageLayoutPosition.start === 1 ? 'start' : 'end';
    const hasImageInset = staticContent.image.imageInset;
    const layoutSize = getLayoutSize(
      staticContent.layout,
      staticContent.image.imageLayoutPosition,
      hasInteractiveContents,
      hasImageInset,
    );

    return {
      hasImage: true,
      partialImageLayout: {
        image: {
          url: image.url,
          altText: image.altText ?? '',
        },
        hasImageInset,
        snapDirection,
        imagePosition,
        layoutSize,
      },
    };
  }
  return {
    hasImage: false,
  };
};
